import React, { useState, useCallback, useEffect } from 'react';
import PlaidLinkComponent from './PlaidLink';
import { plaidAPI } from '../services/api';

const BankAccounts = () => {
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [plaidAccounts, setPlaidAccounts] = useState([]);
  const [ormAccounts, setORMAccounts] = useState([]);
  const [mapping, setMapping] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const [plaidRes, ormRes] = await Promise.all([
        plaidAPI.listPlaidAccounts(),
        plaidAPI.listORMAccounts()
      ]);
      setPlaidAccounts(plaidRes);
      setORMAccounts(ormRes);
    } catch (err) {
      console.error('Error fetching accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handlePlaidSuccess = useCallback((result) => {
    setMessage(`Successfully connected to your bank!`);
    fetchData();
    handleSyncTransactions();
  }, []);

  const handlePlaidError = useCallback((error) => {
    setMessage(`Error connecting to bank: ${error.message || 'Unknown error'}`);
  }, []);

  const handleSyncTransactions = async () => {
    setSyncing(true);
    setMessage('');

    try {
      const result = await plaidAPI.syncTransactions();
      
      if (result.error === 'PRODUCT_NOT_READY') {
        setMessage('Plaid is still preparing your transaction history. This usually takes 1-2 minutes for new connections. Please try again in a moment.');
      } else {
        const total = result.added || result.transactions_synced || 0;
        setMessage(`Successfully synced ${total} new transactions! They are now in the staging area.`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message;
      if (errorMsg.includes('PRODUCT_NOT_READY')) {
        setMessage('Plaid is still preparing your transaction history. This usually takes 1-2 minutes for new connections. Please try again in a moment.');
      } else {
        setMessage(`Error syncing transactions: ${errorMsg}`);
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleLink = async (plaidAccountId, ormId, ormType) => {
    try {
      await plaidAPI.linkAccount(plaidAccountId, ormId, ormType);
      setMessage('Account linked successfully!');
      fetchData();
    } catch (err) {
      setMessage(`Error linking account: ${err.message}`);
    }
  };

  const handleMigrate = async () => {
    setSyncing(true);
    try {
      const result = await plaidAPI.migrateTransactions();
      setMessage(`Safety Migration Complete! Moved ${result.migrated} transactions to your live history.`);
    } catch (err) {
      setMessage(`Migration failed: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="page-container p-6 bg-black min-h-screen text-white">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-bold">Bank Account Integration</h2>
        <div className="text-xs text-gray-500 bg-gray-900 px-3 py-1 rounded-full border border-gray-800">
           v2.2-MAPPING
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Connection Control */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 shadow-2xl">
            <h3 className="text-xl font-semibold mb-2 text-blue-400">Step 1: Connect</h3>
            <p className="text-gray-400 text-sm mb-6">
              Establish a secure link with your bank to pull live data.
            </p>
            <div className="flex flex-col gap-4">
              <PlaidLinkComponent
                onSuccess={handlePlaidSuccess}
                onError={handlePlaidError}
              />
              <button
                onClick={handleSyncTransactions}
                disabled={syncing}
                className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-800 text-white font-bold py-3 px-4 rounded-xl transition-all shadow-lg"
              >
                {syncing ? 'Syncing...' : 'Sync Transactions'}
              </button>
            </div>
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h3 className="text-xl font-semibold mb-2 text-green-400">Step 3: Migrate</h3>
            <p className="text-gray-400 text-sm mb-6">
              Move synced transactions into your main balance sheet history using the Safety Guard.
            </p>
            <button
              onClick={handleMigrate}
              disabled={syncing || plaidAccounts.length === 0}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-800 text-white font-bold py-3 px-4 rounded-xl transition-all shadow-lg"
            >
              Run Safety Migration
            </button>
          </div>

          {message && (
            <div className={`p-4 rounded-xl border text-sm font-medium ${
              message.includes('Error') || message.includes('failed') 
                ? 'bg-red-900/20 text-red-400 border-red-800/50' 
                : 'bg-blue-900/20 text-blue-400 border-blue-800/50'
            }`}>
              {message}
            </div>
          )}
        </div>

        {/* Mapping Table */}
        <div className="lg:col-span-2">
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-gray-800 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-semibold text-white">Step 2: Link Your Accounts</h3>
                <p className="text-gray-400 text-sm mt-1">Associate Plaid accounts with your existing database records.</p>
              </div>
              <button onClick={fetchData} className="text-gray-500 hover:text-white transition-colors">
                Refresh List
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-black/50 text-gray-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-6 py-4 font-semibold">Bank Account (Plaid)</th>
                    <th className="px-6 py-4 font-semibold">Institution</th>
                    <th className="px-6 py-4 font-semibold">Linked To (Database)</th>
                    <th className="px-6 py-4 font-semibold text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {loading ? (
                    <tr><td colSpan="4" className="px-6 py-12 text-center text-gray-500">Loading accounts...</td></tr>
                  ) : plaidAccounts.length === 0 ? (
                    <tr><td colSpan="4" className="px-6 py-12 text-center text-gray-500">No accounts connected yet. Use Step 1 above.</td></tr>
                  ) : (
                    plaidAccounts.map((account) => (
                      <tr key={account.id} className="hover:bg-black/30 transition-colors group">
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-200">{account.name}</div>
                          <div className="text-xs text-gray-500">{account.type} • {account.subtype}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-400">{account.institution}</td>
                        <td className="px-6 py-4">
                          {account.linked_to ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900/30 text-green-400 border border-green-800/50">
                              {account.linked_to}
                            </span>
                          ) : (
                            <select
                              className="bg-black border border-gray-700 rounded px-2 py-1 text-sm text-gray-300 focus:outline-none focus:border-blue-500 w-full"
                              onChange={(e) => {
                                const val = e.target.value;
                                if (!val) return;
                                const [type, id] = val.split(':');
                                setMapping({...mapping, [account.plaid_account_id]: {id, type}});
                              }}
                            >
                              <option value="">-- Select DB Account --</option>
                              {ormAccounts.map(orm => (
                                <option key={`${orm.type}:${orm.id}`} value={`${orm.type}:${orm.id}`}>
                                  {orm.name}
                                </option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          {!account.linked_to && (
                            <button
                              onClick={() => {
                                const m = mapping[account.plaid_account_id];
                                if (m) handleLink(account.plaid_account_id, m.id, m.type);
                              }}
                              disabled={!mapping[account.plaid_account_id]}
                              className="text-blue-500 hover:text-blue-400 text-sm font-semibold disabled:text-gray-700 transition-colors"
                            >
                              Link
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankAccounts;
